import { useCallback, type RefObject } from "react";
import { useBlocker, BlockerFunction, useSearchParams } from "react-router-dom";
import {
  useCompanyFilters,
  type UploadedAccountsSlice,
} from "@/lib/company-filter";
import { useCompanyFiltersStore } from "@/lib/company-filter/provider/CompanyFilterProvider";

interface UseSegmentNavigationBlockerResult {
  isBlocked: boolean;
  proceed: () => void;
  reset: () => void;
}

export function useSegmentNavigationBlocker(
  isSavingRef: RefObject<boolean>,
  // Scoring lives in a separate store; the page passes its dirtiness in so a
  // profile change alone still blocks navigation away (RGI-2264).
  extraUnsavedChanges = false
): UseSegmentNavigationBlockerResult {
  const { hasUnsavedChanges, appliedFilters } = useCompanyFilters();
  const [searchParams] = useSearchParams();
  const isCreateMode = searchParams.get("action") === "new";

  // Check if uploadedAccounts is in active processing state
  const uploadedAccountsSlice = useCompanyFiltersStore(
    (s) => s.slices.uploadedAccounts
  ) as UploadedAccountsSlice;
  const knownCompaniesState = useCompanyFiltersStore(
    (s) => s.slices.knownCompanies.uploadState
  );
  const isUploadProcessing =
    uploadedAccountsSlice.uploadState === "UPLOADING" ||
    uploadedAccountsSlice.uploadState === "PROCESSING" ||
    knownCompaniesState === "UPLOADING" ||
    knownCompaniesState === "PROCESSING";

  // For Create Mode: check if user has applied any filters
  // For Edit Mode: use hasUnsavedChanges (compares applied vs persisted)
  // Also block during active upload/processing to prevent data loss
  const hasChangesToProtect = isCreateMode
    ? (appliedFilters !== undefined &&
        Object.keys(appliedFilters).length > 0) ||
      isUploadProcessing
    : hasUnsavedChanges || extraUnsavedChanges || isUploadProcessing;

  const shouldBlockNavigation: BlockerFunction = useCallback(
    ({ currentLocation, nextLocation }) => {
      // Don't block if no changes to protect or currently saving
      // Use ref for synchronous check (state updates are async)
      if (!hasChangesToProtect || isSavingRef.current) return false;

      // A query-only change on the same page (e.g. syncing filters/scoring or
      // search into the URL for shareable links) is not leaving the page.
      if (nextLocation.pathname === currentLocation.pathname) return false;

      // Block if navigating away from current explore page
      const isStayingOnExplorePage =
        nextLocation.pathname === "/segments/explore" ||
        nextLocation.pathname.startsWith("/segments/explore");

      return !isStayingOnExplorePage;
    },
    [hasChangesToProtect, isSavingRef]
  );

  const blocker = useBlocker(shouldBlockNavigation);

  return {
    isBlocked: blocker.state === "blocked",
    proceed: blocker.proceed ?? (() => {}),
    reset: blocker.reset ?? (() => {}),
  };
}
